import { Immutable } from "immer";
import React from "react";
import { DynamicPrice } from "tsi-common-react/src/components/DynamicPrice/index";
import { IProduct } from "tsi-common-react/src/models/catalogue";
import { t } from "ttag";

/**
 * Posturepedic comparison-chart ports of the deleted
 * ``cms/blocks/hc-block/hc-block--posturepedic-*.html`` partials (#33285).
 *
 * The static table markup is reproduced verbatim (classes / GTM hooks / copy
 * unchanged). Each chart's "Starting At" / "Price" row rendered a
 * ``{% include_react "...DynamicPriceTemplate" product=..._json %}`` island;
 * here those become ``<ChartPrice>`` -- the DynamicPrice component composed
 * directly (it is purely presentational). The per-slot product-API JSON is
 * resolved server-side by ``HardcodedBlock.get_data_for_client_render`` in the
 * same column order these components index.
 */

type IChartProps = Immutable<{
    products: readonly (string | null)[];
}>;

// One chart price cell. Reproduces the legacy static
// ``<div class="hc-block-price-container">`` wrapper (always rendered) with the
// DynamicPrice island inside it -- parsing the product-API JSON exactly as the
// DynamicPriceTemplate wrapper did. A null slot (product not found) renders the
// empty container, matching the legacy island's empty render.
const ChartPrice = (
    props: Immutable<{ product: string | null }>,
): React.JSX.Element => {
    const product = props.product
        ? (() => {
              try {
                  return JSON.parse(props.product) as IProduct;
              } catch {
                  return null;
              }
          })()
        : null;
    return (
        <div className="hc-block-price-container">
            {product && <DynamicPrice productPrice={product.price} />}
        </div>
    );
};

export const PosturepedicEliteHybrid = (
    props: IChartProps,
): React.JSX.Element => {
    const { products } = props;
    return (
        <div>
            <header>
                <section className="aligned-header-section">
                    <div className="rich-text">
                        <h3>Personalized Sleep</h3>
                        <h1>Choose Your Comfort Level</h1>
                    </div>
                </section>
            </header>
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">
                                Sealy<sup>®</sup>{" "}
                                <br className="mobile-break" />
                                Technology
                            </th>
                            <th scope="col" className="support-types">
                                <div className="support-types__content">
                                    <div className="top">
                                        <span className="support-banner support-banner--spring">
                                            Good
                                        </span>
                                        <span className="support-type">
                                            <span>Albany II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className="support-types">
                                <div className="support-types__content">
                                    <div className="top">
                                        <span className="support-banner support-banner--hybrid">
                                            Better
                                        </span>
                                        <span className="support-type">
                                            <span>Brenham II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className="support-types">
                                <div className="support-types__content">
                                    <div className="top">
                                        <span className="support-banner support-banner--best">
                                            Best
                                        </span>
                                        <span className="support-type">
                                            <span>High Point II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Profile <br className="mobile-break" />
                                Height
                            </th>
                            <td>13&quot;</td>
                            <td>14&quot;</td>
                            <td>14&quot;</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Cooling</th>
                            <td>Cooling Cover</td>
                            <td>Cooling Cover</td>
                            <td>Advanced Cooling Cover + Cooling Foam</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Performance <br className="mobile-break" />
                                Foams
                            </th>
                            <td>2&quot; Gel Memory Foam Layer</td>
                            <td>2.5&quot; Gel Memory Foam Layer</td>
                            <td>Up to 3.5&quot; Gel Memory Foam Layer</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Available <br className="mobile-break" />
                                Feels
                            </th>
                            <td className="medium-col">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon medium"></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="medium-col multiple-feels">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon firm"></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="goast-line"></div>
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon soft"></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="medium-col multiple-feels">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon firm"></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon medium"></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon soft"></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Starting At</th>
                            <td>
                                <ChartPrice product={products[0] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[1] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[2] ?? null} />
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const PosturepedicEliteSpring = (
    props: IChartProps,
): React.JSX.Element => {
    const { products } = props;
    return (
        <div>
            <header>
                <section className="aligned-header-section">
                    <div className="rich-text">
                        <h3>Personalized Sleep</h3>
                        <h1>Choose Your Comfort Level</h1>
                    </div>
                </section>
            </header>
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">
                                Sealy<sup>®</sup>{" "}
                                <br className="mobile-break" />
                                Technology
                            </th>
                            <th scope="col" className="support-types">
                                <div className="support-types__content">
                                    <div className="top">
                                        <span className="support-banner support-banner--spring">
                                            Good
                                        </span>
                                        <span className="support-type">
                                            <span>Albany II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className="support-types">
                                <div className="support-types__content">
                                    <div className="top">
                                        <span className="support-banner support-banner--hybrid">
                                            Better
                                        </span>
                                        <span className="support-type">
                                            <span>Brenham II</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Profile <br className="mobile-break" />
                                Height
                            </th>
                            <td>14&quot;–15&quot;</td>
                            <td>15&quot;–16&quot;</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Cooling</th>
                            <td>Cooling Cover</td>
                            <td>Cooling Cover</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Performance <br className="mobile-break" />
                                Foams
                            </th>
                            <td>Up to 2.5&quot; Gel Foam Layer</td>
                            <td>Up to 3&quot; Gel Foam Layer</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Tight <br className="mobile-break" />
                                Top Feels
                            </th>
                            <td className="medium-col">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon firm"></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="medium-col">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon medium"></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Pillowtop <br className="mobile-break" />
                                Feels
                            </th>
                            <td className="medium-col">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon soft"></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="medium-col multiple-feels">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon firm"></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon medium"></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon soft"></span>
                                            <span>Soft</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Starting At</th>
                            <td>
                                <ChartPrice product={products[0] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[1] ?? null} />
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const PosturepedicSpringHybridStandard = (
    props: IChartProps,
): React.JSX.Element => {
    const { products } = props;
    return (
        <div>
            <header>
                <section className="aligned-header-section">
                    <div className="rich-text">
                        <h4>
                            Discover the Sealy Posturepedic<sup>®</sup>{" "}
                            Collection
                        </h4>
                        <h1>Spring vs. Hybrid</h1>
                    </div>
                </section>
            </header>
            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th scope="col"></th>
                            <th scope="col">
                                Sealy<sup>®</sup>{" "}
                                <br className="mobile-break" />
                                Technology
                            </th>
                            <th scope="col" className="support-types">
                                <div className="support-types__content">
                                    <div className="top">
                                        <span className="support-banner support-banner--spring">
                                            Posturepedic<sup>®</sup> Spring
                                        </span>
                                        <span className="support-type">
                                            <span>Ultra&nbsp;</span>
                                            <span>Responsive</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col" className="support-types">
                                <div className="support-types__content">
                                    <div className="top">
                                        <span className="support-banner support-banner--hybrid">
                                            Posturepedic<sup>®</sup> Hybrid
                                        </span>
                                        <span className="support-type">
                                            <span>Premium&nbsp;</span>
                                            <span>Pressure Relief</span>
                                        </span>
                                    </div>
                                </div>
                            </th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Profile <br className="mobile-break" />
                                Height
                            </th>
                            <td>13”</td>
                            <td>12”</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Coils</th>
                            <td>
                                <span
                                    className="check-icon"
                                    aria-hidden="true"
                                ></span>
                                <span className="ada-screenreader-only">
                                    {t`Yes`}
                                </span>
                            </td>
                            <td>
                                <span
                                    className="check-icon"
                                    aria-hidden="true"
                                ></span>
                                <span className="ada-screenreader-only">
                                    {t`Yes`}
                                </span>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Foam <br className="mobile-break" />
                                Layers
                            </th>
                            <td>
                                <span
                                    className="check-icon"
                                    aria-hidden="true"
                                ></span>
                                <span className="ada-screenreader-only">
                                    {t`Yes`}
                                </span>
                            </td>
                            <td>
                                <div className="two-rows row-with-check-icon">
                                    <span
                                        className="check-icon"
                                        aria-hidden="true"
                                    ></span>
                                    <span className="ada-screenreader-only">
                                        {t`Yes`}
                                    </span>
                                    <span>2x more conforming foam</span>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">Support</th>
                            <td>
                                <div className="two-rows">
                                    <span>
                                        <b>Center Support</b>
                                    </span>
                                    <span className="copy-gap copy-alignment">
                                        <span>
                                            maintains natural body alignment
                                        </span>
                                    </span>
                                </div>
                            </td>
                            <td>
                                <div className="two-rows">
                                    <span className="title-alignment">
                                        <span>
                                            <b>ComfortSense™</b>
                                        </span>
                                    </span>
                                    <span className="copy-gap copy-alignment">
                                        <span>
                                            provides maximum conforming comfort
                                        </span>
                                    </span>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Available <br className="mobile-break" />
                                Feels
                            </th>
                            <td className="medium-col">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon medium"></span>
                                            <span>Medium</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="medium-col">
                                <div className="available-feels-container">
                                    <div className="wrapper">
                                        <div className="available-feels">
                                            <span className="available-feels__icon medium"></span>
                                            <span>Medium</span>
                                        </div>
                                        <div className="available-feels">
                                            <span className="available-feels__icon firm"></span>
                                            <span>Firm</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td></td>
                        </tr>
                        <tr>
                            <td></td>
                            <th scope="row">
                                Price <br className="mobile-break" />
                                (Queen)
                            </th>
                            <td>
                                <ChartPrice product={products[0] ?? null} />
                            </td>
                            <td>
                                <ChartPrice product={products[1] ?? null} />
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};
